import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useHistory, useLocation} from 'react-router-dom'
import './styles/auditoria.css'
import {ILaudoAuditoria, ILaudoCapitulo} from './interfaces/ILaudoAuditoria'
import {LaudoAuditoriaService} from './services/LaudoAuditoriaService'
import AuditoriaLaudoPainel from './components/AuditoriaLaudoPainel'
import AuditoriaLaudoResponsaveis from './components/AuditoriaLaudoResponsaveis'
import AuditoriaLaudoBarraProgressao, {
  rotuloCapitulo,
} from './components/AuditoriaLaudoBarraProgressao'
import {exportarLaudoEBaixar} from './utils/exportarLaudo'
import {
  capitulosDaRevisao,
  EstadoRevisao,
  estadoRevisao,
  laudoEmAndamento,
  proximoPendente,
} from './utils/laudoRevisao'

const ESTADOS: Record<EstadoRevisao, {marcador: string; texto: string; cor?: string}> = {
  ACEITO: {marcador: '✓', texto: 'ACEITO', cor: '#2e7d32'},
  RECUSADO: {marcador: '✕', texto: 'RECUSADO', cor: '#c62828'},
  FALHA: {marcador: '!', texto: 'FALHA NA GERAÇÃO', cor: '#c62828'},
  GERANDO: {marcador: '…', texto: 'GERANDO'},
  PENDENTE: {marcador: '○', texto: 'PENDENTE', cor: '#8a6d1d'},
}

// DEVN3-521 — tela de revisão do Laudo IA (/auditoria/revisao?empresa=&periodo=). A primeira
// etapa é definir os responsáveis do laudo; só então os capítulos são liberados. Lista todos os
// capítulos com apontamento (ERRO, ALERTA ou AVISO) à esquerda e o capítulo selecionado à direita.
// O revisor aceita ou recusa cada um; quando não sobra pendente, o export libera e o PDF leva só
// os aceitos. O `periodo` é o id da esteira, como em /auditoria.
const AuditoriaRevisaoPage: React.FC = () => {
  const history = useHistory()
  const {search} = useLocation()
  const params = useMemo(() => new URLSearchParams(search), [search])
  const periodo = params.get('periodo') ?? ''
  const empresa = params.get('empresa') ?? ''

  const [laudo, setLaudo] = useState<ILaudoAuditoria | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string>()
  const [selecionadoId, setSelecionadoId] = useState<number>()
  const [exportando, setExportando] = useState(false)

  const recarregar = useCallback(async () => {
    const atual = await LaudoAuditoriaService.buscar(periodo)
    setLaudo(atual)
    return atual
  }, [periodo])

  useEffect(() => {
    if (!periodo) {
      setCarregando(false)
      return
    }
    let cancelado = false
    ;(async () => {
      try {
        // Idempotente: cobre o acesso direto pela URL, sem ter passado por /auditoria.
        await LaudoAuditoriaService.garantir(periodo)
        const atual = await LaudoAuditoriaService.buscar(periodo)
        if (!cancelado) setLaudo(atual)
      } catch (e: any) {
        if (!cancelado)
          setErro(e?.response?.data?.mensagem ?? 'Não foi possível carregar o laudo deste período.')
      } finally {
        if (!cancelado) setCarregando(false)
      }
    })()
    return () => {
      cancelado = true
    }
  }, [periodo])

  // Enquanto houver geração/regeneração em andamento, consulta de novo a cada 8s. O export tem
  // o próprio acompanhamento (exportarLaudoEBaixar).
  useEffect(() => {
    if (!laudo || exportando || !laudoEmAndamento(laudo)) return
    const timer = setTimeout(() => {
      recarregar().catch(() => undefined)
    }, 8000)
    return () => clearTimeout(timer)
  }, [laudo, exportando, recarregar])

  const capitulos = useMemo(() => (laudo ? capitulosDaRevisao(laudo) : []), [laudo])
  const selecionado: ILaudoCapitulo | undefined =
    capitulos.find((c) => c.id === selecionadoId) ?? proximoPendente(capitulos) ?? capitulos[0]

  // Depois de aceitar ou recusar: recarrega e pula para o próximo capítulo ainda pendente.
  const aoRevisar = async (capituloId: number) => {
    const atual = await recarregar()
    const proximo = atual ? proximoPendente(capitulosDaRevisao(atual), capituloId) : undefined
    setSelecionadoId(proximo?.id ?? capituloId)
  }

  const exportar = async (formato: 'PDF' | 'DOCX') => {
    if (!laudo?.exportHabilitado) return
    setExportando(true)
    setErro(undefined)
    setErro(await exportarLaudoEBaixar(periodo, formato, setLaudo))
    setExportando(false)
  }

  const voltar = () => {
    const destino = new URLSearchParams()
    if (empresa) destino.set('empresa', empresa)
    if (periodo) destino.set('periodo', periodo)
    history.push(`/auditoria?${destino.toString()}`)
  }

  const contratoRef = capitulos.find((c) => c.contrato)?.contrato
  const responsaveisDefinidos = !!laudo?.responsaveis?.configurado
  const exportOcupado = exportando || !!laudo?.arquivoRetornoProcessando

  return (
    <div className='auditoria-page'>
      <div className='auditoria-header-card'>
        <div className='d-flex justify-content-between align-items-start'>
          <div>
            <h2 className='auditoria-title'>Revisão do Laudo</h2>
            <p className='auditoria-subtitle mb-0'>
              {contratoRef
                ? `${contratoRef.empresa} · ${contratoRef.periodo}`
                : 'Aceite ou recuse cada capítulo redigido pela IA. Só os aceitos entram no PDF.'}
            </p>
          </div>
          <button className='btn btn-sm btn-outline-secondary' onClick={voltar}>
            Voltar para a auditoria
          </button>
        </div>
      </div>

      {erro && <div className='alert alert-danger'>{erro}</div>}

      {carregando && <div className='auditoria-card'>Carregando...</div>}

      {!carregando && !laudo && !erro && (
        <div className='auditoria-card auditoria-empty-state'>
          Selecione uma empresa e um período na tela de auditoria para revisar o laudo.
        </div>
      )}

      {!carregando && laudo && (
        <div className='auditoria-layout auditoria-revisao-layout'>
          <aside className='auditoria-sidebar-card'>
            <AuditoriaLaudoBarraProgressao laudo={laudo} />

            <ul className='list-unstyled auditoria-revisao-lista'>
              {capitulos.map((c) => {
                const e = ESTADOS[estadoRevisao(c)]
                const ativo = c.id === selecionado?.id
                return (
                  <li key={c.id}>
                    <button
                      type='button'
                      className={`auditoria-revisao-item ${ativo ? 'ativo' : ''}`}
                      disabled={!responsaveisDefinidos}
                      onClick={() => setSelecionadoId(c.id)}
                    >
                      <span style={{color: e.cor}}>{e.marcador}</span> {rotuloCapitulo(c)}
                      <span className='d-block auditoria-revisao-item-estado'>
                        {c.contrato?.status && (
                          <span className={`auditoria-status status-${c.contrato.status}`}>
                            {c.contrato.status}
                          </span>
                        )}
                        <span style={{color: e.cor ?? '#8b97a3'}}>{e.texto}</span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>

            <div
              className='btn-group w-100 mt-3'
              title={
                laudo.exportHabilitado
                  ? ''
                  : !responsaveisDefinidos
                  ? 'Defina os responsáveis do laudo para liberar a exportação.'
                  : 'Aceite ou recuse todos os capítulos para liberar a exportação.'
              }
            >
              <button
                className='btn btn-sm btn-primary'
                disabled={!laudo.exportHabilitado || exportOcupado}
                onClick={() => exportar('PDF')}
              >
                {exportOcupado && <span className='spinner-border spinner-border-sm me-1' />}
                Exportar Laudo (PDF)
              </button>
              <button
                className='btn btn-sm btn-outline-primary'
                disabled={!laudo.exportHabilitado || exportOcupado}
                onClick={() => exportar('DOCX')}
              >
                Word
              </button>
            </div>
          </aside>

          <main>
            <AuditoriaLaudoResponsaveis
              periodo={periodo}
              responsaveis={laudo.responsaveis ?? {configurado: false, sugestao: false}}
              onSalvo={setLaudo}
            />

            {!responsaveisDefinidos ? (
              <div className='auditoria-card auditoria-empty-state'>
                Defina os responsáveis do laudo para começar a revisão dos capítulos.
              </div>
            ) : selecionado ? (
              <AuditoriaLaudoPainel
                key={selecionado.id}
                capitulo={selecionado}
                onRecarregar={async () => {
                  await recarregar()
                }}
                onRevisado={aoRevisar}
              />
            ) : (
              <div className='auditoria-card auditoria-empty-state'>
                {laudo.status === 'GERANDO'
                  ? 'A IA ainda está analisando as regras de auditoria. Os capítulos aparecem aqui conforme ficam prontos.'
                  : 'Nenhum apontamento identificado neste período — não há capítulos para revisar.'}
              </div>
            )}
          </main>
        </div>
      )}
    </div>
  )
}

export default AuditoriaRevisaoPage
